import * as R from 'ramda';
import {
  format as dfFormat,
  parseISO,
  isValid as dfIsValid,
  isDate as dfIsDate,
  getMonth as dfGetMonth,
  getYear as dfGetYear,
  startOfISOWeek,
  endOfISOWeek,
  endOfWeek,
  endOfYear,
  endOfQuarter,
  endOfMonth,
  endOfDay,
  endOfHour,
  endOfSecond,
  add,
  addWeeks as dfAddWeeks,
  addMonths as dfAddMonths,
  addQuarters as dfAddQuarters,
  addYears as dfAddYears,
  addDays as dfAddDays,
  addHours as dfAddHours,
  addMinutes as dfAddMinutes,
  addSeconds as dfAddSeconds,
  differenceInYears,
  differenceInMonths,
  differenceInQuarters,
  differenceInWeeks,
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  compareAsc,
} from 'date-fns';

import { defaultFrequency } from '../settings';

// ------------------------------------------------------------------
export const sdmxFormat = {
  A: 'yyyy', // Year
  S: 'yyyy-',
  Q: "yyyy-'Q'Q",
  M: 'yyyy-MM',
  // ISO week padded (II = 2-digit ISO week); include literal 'W'
  W: "yyyy-'W'II",
  B: "yyyy-'W'II",
  D: 'yyyy-MM-dd',
  H: "yyyy-MM-dd'T'HH:mm:ss",
  N: "yyyy-MM-dd'T'HH:mm:ss",
};

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------
const toDateSafe = (d) => {
  if (dfIsDate(d)) return d;
  if (R.is(Number, d)) return new Date(d);
  if (R.is(String, d)) {
    const parsed = parseISO(d);
    return dfIsValid(parsed) ? parsed : new Date(d);
  }
  return d;
};

const getIsValidDate = (dateStrOrDate) => {
  if (R.isNil(dateStrOrDate)) return false;
  const d = toDateSafe(dateStrOrDate);
  return dfIsDate(d) && dfIsValid(d);
};

// const getIsValidDate = (dateStr) =>
//   R.and(isDate, (date) => R.not(R.equals(getTime(date), NaN)))(
//     parseISO(dateStr),
//   );
// ------------------------------------------------------------------
const getSemesterValue = R.ifElse(
  R.pipe(Number, R.inc, R.gte(6)),
  R.always(1),
  R.always(2),
);

// ------------------------------------------------------------------
const isWeekInPreviousYear = (date) => {
  const startIsoWeek = startOfISOWeek(toDateSafe(date));
  // check the years of days Monday..Thursday of that ISO week (4 days)
  const years = R.times((n) => dfGetYear(dfAddDays(startIsoWeek, n)), 4);
  return R.uniq(years).length === 1 && R.head(years) < dfGetYear(date);
};

// ------------------------------------------------------------------
export const getEndOfYear = (date) => {
  const d = toDateSafe(date);
  if (!getIsValidDate(d)) return undefined;
  return endOfYear(d);
};

// ------------------------------------------------------------------
export const getAjustedDate = (frequency) => (date) => {
  const d = toDateSafe(date);
  if (R.isNil(d) || !getIsValidDate(d)) return d;

  if (frequency === 'W' || frequency === 'B') {
    return isWeekInPreviousYear(d) ? startOfISOWeek(d) : endOfISOWeek(d);
  }
  return d;
};

// ------------------------------------------------------------------
export const applyFormat =
  (frequency, formats = sdmxFormat, options = {}) =>
  (date) => {
    if (R.isNil(date)) return date;
    const hasFormat = R.includes(frequency)(R.keys(formats));
    const format = hasFormat
      ? R.prop(frequency)(formats)
      : R.prop(defaultFrequency)(formats);

    return R.pipe(getAjustedDate(frequency), (d) => {
      let formatted = dfFormat(d, format, options);
      if (frequency === 'S') {
        const semester = `S${getSemesterValue(dfGetMonth(d))}`;
        formatted = R.join('', [formatted, semester]);
      }
      return formatted;
    })(date);
  };

// ------------------------------------------------------------------
const parseYear = (y) => new Date(Number(y), 0, 1);

const addMonths = (date, n) => add(date, { months: n });

const slicerSQW = R.pipe(R.last, R.slice(1, Infinity));

const getSQW = (fn, slicer) =>
  R.map((period) => {
    if (R.isEmpty(R.head(period))) return undefined;
    const year = R.head(period);
    const hasAdded = R.pipe(R.length, R.equals(2))(period) ? slicer(period) : 1;
    const res = R.pipe((y) => parseYear(y), fn(Number(hasAdded)))(year);
    return res;
  });

// ------------------------------------------------------------------
const adjustEndDate = (freq) => (date) => {
  if (R.isNil(date)) return date;
  const d = toDateSafe(date);
  if (!getIsValidDate(d)) return undefined;

  const adjusters = {
    A: endOfYear,
    S: R.pipe((dd) => add(dd, { months: 5 }), endOfMonth),
    Q: endOfQuarter,
    M: endOfMonth,
    W: endOfWeek,
    B: endOfWeek,
    D: endOfDay,
    H: endOfHour,
    N: endOfSecond,
  };

  const adjuster = adjusters[freq];
  if (R.isNil(adjuster)) return d;
  return adjuster(d);
};
// ------------------------------------------------------------------
export const parseDateFromSdmxPeriod = R.curry((frequency, sdmxPeriod) => {
  if (R.isNil(sdmxPeriod)) return sdmxPeriod;

  const periodSQW = R.map((stringPeriod = '') => R.split('-')(stringPeriod))(
    sdmxPeriod,
  );

  let res;

  if (!R.includes(frequency, ['Q', 'S', 'W', 'B'])) {
    res = R.map((p) => {
      if (R.isEmpty(p)) return undefined;
      const d = toDateSafe(p);
      return d;
    })(sdmxPeriod);
  } else if (R.includes(frequency, ['W', 'B'])) {
    res = R.pipe(
      getSQW((n) => (d) => dfAddWeeks(d, n - 1), slicerSQW),
      R.map(getAjustedDate(frequency)),
    )(periodSQW);
  } else if (R.equals('Q', frequency)) {
    res = getSQW((n) => (d) => dfAddQuarters(d, n - 1), slicerSQW)(periodSQW);
  } else {
    // semester
    res = getSQW((n) => (d) => addMonths(d, (n - 1) * 6), slicerSQW)(periodSQW);
  }

  return (
    R.map(R.when(R.identity, R.over(R.lensIndex(1), adjustEndDate(frequency)))),
    res
  );
});
// ------------------------------------------------------------------
// Difference and adders
// ------------------------------------------------------------------
const getDifferenceBetweenDates = (frequency, interval) => {
  const differs = {
    A: differenceInYears,
    S: R.pipe(differenceInMonths, (m) => Math.floor(m / 6)),
    Q: differenceInQuarters,
    M: differenceInMonths,
    W: differenceInWeeks,
    B: differenceInWeeks,
    D: differenceInDays,
    H: differenceInHours,
    N: differenceInMinutes,
  };

  const fn = differs[frequency] || differenceInYears;
  return fn(R.last(interval), R.head(interval));
};

const addTimeList = {
  A: (date, v) => dfAddYears(date, v),
  S: (date, v) => dfAddMonths(date, v * 6),
  Q: (date, v) => dfAddQuarters(date, v),
  M: (date, v) => dfAddMonths(date, v),
  W: (date, v) => dfAddWeeks(date, v),
  B: (date, v) => dfAddWeeks(date, v),
  D: (date, v) => dfAddDays(date, v),
  H: (date, v) => dfAddHours(date, v),
  N: (date, v) => dfAddMinutes(date, v),
};

// ------------------------------------------------------------------
// Helper wrappers to keep original behaviour
// ------------------------------------------------------------------
export const getFrequencyFromSdmxPeriod = (sdmxPeriod) => {
  if (R.isNil(sdmxPeriod)) return null;
  return R.reduce((memo, sdmxDate) => {
    if (R.either(R.isNil, R.isEmpty)(sdmxDate)) return memo;
    const splitedSdmxDate = R.split('-')(sdmxDate);
    const potentialFrequency = R.pipe(
      R.nth(1),
      R.when(R.isNil, R.always('')),
      R.head,
    )(splitedSdmxDate);
    if (R.includes(potentialFrequency, ['Q', 'S', 'W', 'B']))
      return potentialFrequency;
    if (R.length(splitedSdmxDate) === 2) return 'M';
    if (R.length(splitedSdmxDate) === 3) return 'D';
    return memo;
  }, defaultFrequency)(sdmxPeriod);
};

export const getFrequencies = R.pipe(
  R.propOr([], 'values'),
  R.map(R.props(['id', 'label'])),
  R.fromPairs,
);

export const parseInclusiveDates =
  (inclusives, frequency = defaultFrequency) =>
  (dates) => {
    const addTime = (date, v) =>
      addTimeList[frequency] ? addTimeList[frequency](date, v) : date;
    return R.addIndex(R.map)((date, index) => {
      if (R.isNil(date)) return date;
      const isInclusive = R.nth(index)(inclusives);
      if (R.either(R.identity, R.isNil)(isInclusive)) return date;
      if (R.equals(0, index)) return addTime(date, 1);
      return addTime(date, -1);
    })(dates);
  };

export const getAscendentDates = (dates) => {
  const startDate = R.head(dates);
  const endDate = R.last(dates);
  if (R.pipe(compareAsc, R.flip(R.lte)(0))(startDate, endDate)) return dates;
  return [undefined, undefined];
};

export const replaceUndefinedDates = (boundaries) =>
  R.addIndex(R.map)((date, index) =>
    getIsValidDate(date) ? date : R.nth(index)(boundaries),
  );

export const getDatesFromSdmxPeriod = R.curry((frequencyType, period) =>
  R.map(R.ifElse(getIsValidDate, R.identity, R.always(undefined)))(
    parseDateFromSdmxPeriod(frequencyType, period),
  ),
);

export const getSdmxPeriod = R.curry((frequency, date) =>
  R.ifElse(getIsValidDate, applyFormat(frequency), R.always(undefined))(date),
);

export const getIntervalPeriod =
  (datesBoundaries) =>
  (frequency, dates = []) => {
    if (R.isNil(dates))
      return [0, getDifferenceBetweenDates(frequency, datesBoundaries) + 1];
    if (R.all(R.isNil)(dates))
      return [0, getDifferenceBetweenDates(frequency, datesBoundaries) + 1];
    return [
      getDifferenceBetweenDates(
        frequency,
        replaceUndefinedDates(datesBoundaries)(dates),
      ) + 1,
      getDifferenceBetweenDates(frequency, datesBoundaries) + 1,
    ];
  };

export const changeFrequency = (frequency) =>
  R.pipe(
    R.keys,
    R.ifElse(R.includes(frequency), R.always(frequency), R.head),
    R.ifElse(
      R.either(R.isEmpty, R.isNil),
      R.always(defaultFrequency),
      R.identity,
    ),
  );

// ------------------------------------------------------------------

export const getSubstructedDate = (date, _value, _freq) => {
  const freq = R.isNil(_freq) ? defaultFrequency : _freq;
  const isValid = getIsValidDate(date);
  const value = Number(_value);

  if (R.isNil(_value) || !isValid) return;
  let newDate = date;
  if (freq === 'A') {
    newDate = dfAddYears(date, -value + 1);
  }
  if (freq === 'S') {
    newDate = dfAddMonths(date, (-value + 1) * 6);
  }
  if (freq === 'Q') {
    newDate = dfAddMonths(date, (-value + 1) * 3);
  }
  if (freq === 'M') {
    newDate = dfAddMonths(date, -value + 1);
  }
  if (freq === 'W' || freq === 'B') {
    newDate = dfAddDays(date, (-value + 1) * 7);
  }
  if (freq === 'D') {
    newDate = dfAddDays(date, -value + 1);
  }
  if (freq === 'H') {
    newDate = dfAddHours(date, -value + 1);
  }
  if (freq === 'N') {
    newDate = dfAddMinutes(date, -value + 1);
  }
  newDate = dfAddSeconds(newDate, -value + 1);
  return newDate;
};
